import React from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  useTheme,
  CircularProgress,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonIcon from "@mui/icons-material/Person";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import ActivityWidget from "./ActivityWidget.js";

const MetricsPanel = ({ metrics, activities = [], loading = false }) => {
  const theme = useTheme();

  const trends = {
    totalProspects: "up",
    newProspects: "up",
  };

  // Define metrics cards data
  const metricCards = [
    {
      id: "totalProspects",
      title: "Total Prospects",
      value: metrics.totalProspects,
      icon: <PersonIcon />,
      color: theme.palette.primary.main,
      trend: trends.totalProspects,
    },
    {
      id: "newProspects",
      title: "New Prospects",
      value: metrics.newProspects,
      icon: <PersonAddIcon />,
      color: theme.palette.success.main,
      trend: trends.newProspects,
    },
  ];

  // Render trend icon based on trend direction
  const renderTrendIcon = (trend) => {
    switch (trend) {
      case "up":
        return (
          <TrendingUpIcon
            fontSize="small"
            sx={{ color: theme.palette.success.main }}
          />
        );
      case "down":
        return (
          <TrendingDownIcon
            fontSize="small"
            sx={{ color: theme.palette.error.main }}
          />
        );
      case "flat":
      default:
        return (
          <TrendingFlatIcon
            fontSize="small"
            sx={{ color: theme.palette.text.secondary }}
          />
        );
    }
  };

  return (
    <Grid container spacing={1}>
      {/* Metrics Cards - Left Side */}
      {metricCards.map((card) => (
        <Grid item xs={12} sm={6} md={3} lg={2} key={card.id}>
          <Paper
            sx={{
              p: 1.5,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              borderTop: `4px solid ${card.color}`,
              borderRadius: 2,
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: 4,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 0.5,
              }}
            >
              <Typography variant="subtitle2" color="text.secondary">
                {card.title}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  bgcolor: `${card.color}20`,
                  color: card.color,
                  mr: 1.5,
                }}
              >
                {card.icon}
              </Box>
              {loading ? (
                <CircularProgress size={24} sx={{ mr: 1.5 }} />
              ) : (
                <Typography variant="h4" component="div" fontWeight="bold">
                  {card.value}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", mt: "auto" }}>
              {renderTrendIcon(card.trend)}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ml: 0.5 }}
              >
                {card.trend === "up"
                  ? "+12%"
                  : card.trend === "down"
                  ? "-8%"
                  : "No change"}{" "}
                from last period
              </Typography>
            </Box>
          </Paper>
        </Grid>
      ))}

      {/* Activity Widget - Right Side - Takes remaining space */}
      <Grid item xs={12} sm={6} md={6} lg={8}>
        <ActivityWidget activities={activities} />
      </Grid>
    </Grid>
  );
};

export default MetricsPanel;
